import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { finalize } from 'rxjs';
import _ from 'lodash';
import { ExpenseConcept } from '../../../models/expense-concept/expense-concept';
import {
  ExpenseReceipt,
  ExpenseReceiptOwner,
  ExpenseReceiptStatus,
  OwnerReference,
  ReceiptOwnerCode,
} from '../../../models/expense-receipt/expense-receipt';
import { ExpenseConceptProxyService } from '../../../proxies/expense-concept.proxy.service';
import { ExpenseReceiptProxyService } from '../../../proxies/expense-receipt.proxy.service';
import { Currency, currencySymbol } from '../../../utilities/currency.util';
import { ReceiptEditorSave } from '../expense-receipt-editor/expense-receipt-editor.component';

const ACCEPTED_FILES = '.pdf,.png,.jpg,.jpeg,.xlsx,.docx';

/**
 * Capture receipts (#5794) for one fund or trip. Each receipt is edited inline and saved on its own; "Upload files"
 * makes one Incomplete receipt per file. Capturing posts nothing: complete receipts wait in the owner's pool for an
 * expense claim. A fund's receipts are in its currency; a trip's may be HNL or USD.
 */
@Component({
  selector: 'app-receipt-capture',
  templateUrl: './receipt-capture.component.html',
})
export class ReceiptCaptureComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly proxy = inject(ExpenseReceiptProxyService);
  private readonly conceptProxy = inject(ExpenseConceptProxyService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly accept = ACCEPTED_FILES;
  readonly statuses = ExpenseReceiptStatus;

  readonly isLoading = signal(0);
  readonly owners = signal<ExpenseReceiptOwner[]>([]);
  readonly owner = signal<ExpenseReceiptOwner | null>(null);
  readonly receipts = signal<ExpenseReceipt[]>([]);
  readonly concepts = signal<ExpenseConcept[]>([]);

  /** The receipt being edited: null = editor closed; 'new' = a receipt not saved yet. */
  readonly editingId = signal<string | 'new' | null>(null);
  readonly editingReceipt = computed(() => this.receipts().find((r) => r.id === this.editingId()) ?? null);
  /** Bumped on every open, so the shared editor resets even when the same receipt is reopened. */
  readonly editKey = signal(0);

  /** The owner switcher (moves every receipt of this capture). */
  readonly moveVisible = signal(false);
  readonly moveTarget = new FormControl<string | null>(null);

  readonly ownerControl = new FormControl<string | null>(null);

  readonly isFund = computed(() => this.owner()?.owner.entityDefinitionCode === ReceiptOwnerCode.PettyCashFund);
  readonly ownerCurrency = computed(() => this.owner()?.currency ?? null);
  readonly ownerLabel = computed(() => {
    const current = this.owner();
    if (_.isNil(current)) return '';
    const kind = this.isFund() ? this.label('Receipt_Owner_Fund', 'Fund') : this.label('Receipt_Owner_Trip', 'Trip');
    return `${kind} · ${current.owner.number} · ${current.name}`;
  });

  readonly moveOptions = computed(() =>
    this.owners()
      .filter((o) => o.owner.reference !== this.owner()?.owner.reference)
      .map((o) => ({ value: o.owner.reference, label: this.optionLabel(o) })),
  );
  readonly ownerOptions = computed(() => this.owners().map((o) => ({ value: o.owner.reference, label: this.optionLabel(o) })));

  readonly kpis = computed(() => {
    const receipts = this.receipts();
    const fundCurrency = this.ownerCurrency();
    // A fund holds one currency; a trip's receipts can mix, so its totals are in USD.
    const total = _.sumBy(receipts, (r) => (_.isNil(fundCurrency) ? (r.totalUsd ?? 0) : r.total));
    const tax = _.sumBy(receipts, (r) => (_.isNil(fundCurrency) ? (r.rateToUsd ? r.taxTotal / r.rateToUsd : 0) : r.taxTotal));
    return {
      count: receipts.length,
      inPool: receipts.filter((r) => r.status === ExpenseReceiptStatus.InPool).length,
      incomplete: receipts.filter((r) => r.status === ExpenseReceiptStatus.Incomplete).length,
      symbol: currencySymbol(fundCurrency ?? Currency.USD),
      tax,
      total,
    };
  });

  ngOnInit(): void {
    this.loadConcepts();
    this.startLoading();
    this.proxy
      .getOwners()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        this.owners.set(response.data ?? []);
        const params = this.route.snapshot.queryParamMap;
        const reference = params.get('ownerReference');
        const selected = this.owners().find((o) => o.owner.reference === reference) ?? null;
        if (!_.isNil(selected)) this.selectOwner(selected);
      });
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private loadConcepts(): void {
    this.conceptProxy.search({ isActive: true }, 1, 200, 'code', 1).subscribe((response) => this.concepts.set(response.data?.items ?? []));
  }

  onOwnerChange(reference: string | null): void {
    const selected = this.owners().find((o) => o.owner.reference === reference) ?? null;
    if (_.isNil(selected)) return;
    this.router.navigate([], {
      queryParams: { ownerReference: selected.owner.reference, ownerCode: selected.owner.entityDefinitionCode },
      replaceUrl: true,
    });
    this.selectOwner(selected);
  }

  private selectOwner(selected: ExpenseReceiptOwner): void {
    this.owner.set(selected);
    this.ownerControl.setValue(selected.owner.reference, { emitEvent: false });
    this.closeEditor();
    this.loadPool();
  }

  private ownerRef(): OwnerReference | null {
    const current = this.owner();
    return _.isNil(current) ? null : { reference: current.owner.reference, entityDefinitionCode: current.owner.entityDefinitionCode };
  }

  private loadPool(): void {
    const owner = this.ownerRef();
    if (_.isNil(owner)) return;
    this.startLoading();
    this.proxy
      .getPool(owner)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => this.receipts.set(response.data?.receipts ?? []));
  }

  // #region Editor

  addReceipt(): void {
    this.openEditor(null);
  }

  editReceipt(receipt: ExpenseReceipt): void {
    this.openEditor(receipt);
  }

  private openEditor(receipt: ExpenseReceipt | null): void {
    this.editingId.set(receipt?.id ?? 'new');
    this.editKey.update((k) => k + 1);
  }

  closeEditor(): void {
    this.editingId.set(null);
  }

  saveReceipt(event: ReceiptEditorSave): void {
    const owner = this.ownerRef();
    if (_.isNil(owner)) return;
    const next = event.next;
    this.startLoading();
    this.proxy
      .saveBatch({ owner, receipts: [event.update] })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const item = response.data?.[0];
        if (_.isNil(item)) return;
        if (_.isNil(item.receipt)) {
          const message = this.translate.currentLang === 'es' ? item.errorEs : item.errorEn;
          GlobalMessageUtil.error(this.label('Receipt_Title', 'Capture receipts'), message ?? '');
          return;
        }
        const saved = item.receipt;
        this.upsert(saved);
        GlobalMessageUtil.success(
          this.label('Receipt_Title', 'Capture receipts'),
          saved.status === ExpenseReceiptStatus.InPool
            ? this.label('Receipt_Saved_Pool', 'Receipt saved to the pool.')
            : this.label('Receipt_Saved_Incomplete', 'Receipt saved as incomplete.'),
        );
        if (next) {
          this.openEditor(null);
        } else {
          this.openEditor(saved);
        }
      });
  }

  // #endregion

  // #region Files

  uploadFiles(event: Event): void {
    const owner = this.ownerRef();
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (_.isNil(owner) || files.length === 0) return;
    this.startLoading();
    this.proxy
      .upload(owner, files)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const created = response.data ?? [];
        this.receipts.update((list) => [...created, ...list]);
        GlobalMessageUtil.success(
          this.label('Receipt_Title', 'Capture receipts'),
          `${created.length} ${this.label('Receipt_Uploaded', 'receipts created from files; complete them with the pencil.')}`,
        );
      });
  }

  attachFile(file: File): void {
    const id = this.editingId();
    if (_.isNil(id) || id === 'new') return;
    this.startLoading();
    this.proxy
      .addAttachment(id, file)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (!_.isNil(response.data)) this.upsert(response.data);
      });
  }

  removeAttachment(name: string): void {
    const id = this.editingId();
    if (_.isNil(id) || id === 'new') return;
    this.startLoading();
    this.proxy
      .removeAttachment(id, name)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (!_.isNil(response.data)) this.upsert(response.data);
      });
  }

  // #endregion

  confirmDelete(receipt: ExpenseReceipt): void {
    this.confirmationService.confirm({
      header: this.label('Receipt_Delete_Title', 'Delete receipt'),
      message: `${this.label('Receipt_Delete_Message', 'Delete this receipt and its files?')} ${receipt.number}`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.label('Delete', 'Delete'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.startLoading();
        this.proxy
          .delete(receipt.id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe(() => {
            this.receipts.update((list) => list.filter((r) => r.id !== receipt.id));
            if (this.editingId() === receipt.id) this.closeEditor();
          });
      },
    });
  }

  openMove(): void {
    this.moveTarget.reset(null);
    this.moveVisible.set(true);
  }

  /** Moves every receipt of this capture to another fund or trip, then follows them there. */
  confirmMove(): void {
    const target = this.owners().find((o) => o.owner.reference === this.moveTarget.value);
    const ids = this.receipts().map((r) => r.id);
    if (_.isNil(target) || ids.length === 0) return;
    this.startLoading();
    this.proxy
      .changeOwner(ids, { reference: target.owner.reference, entityDefinitionCode: target.owner.entityDefinitionCode })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe(() => {
        this.moveVisible.set(false);
        GlobalMessageUtil.success(this.label('Receipt_Title', 'Capture receipts'), this.label('Receipt_Moved', 'Receipts moved.'));
        this.onOwnerChange(target.owner.reference);
      });
  }

  back(): void {
    const current = this.owner();
    if (_.isNil(current)) {
      this.router.navigate(['/petty-cash-funds']);
      return;
    }
    if (this.isFund()) this.router.navigate(['/petty-cash-fund-details'], { queryParams: { id: current.owner.reference } });
    else this.router.navigate(['/trip-details'], { queryParams: { id: current.owner.reference } });
  }

  // #region Display helpers

  firstConcept(receipt: ExpenseReceipt): string {
    const concepts = _.uniq(receipt.lines.map((l) => l.concept?.name).filter((n): n is string => !_.isNil(n)));
    if (concepts.length === 0) return '—';
    return concepts.length === 1 ? concepts[0] : `${concepts[0]} +${concepts.length - 1}`;
  }

  symbolOf(currency: Currency | null | undefined): string {
    return currencySymbol(currency);
  }

  statusLabel(status: ExpenseReceiptStatus): string {
    switch (status) {
      case ExpenseReceiptStatus.Incomplete:
        return this.label('Receipt_Status_Incomplete', 'Incomplete');
      case ExpenseReceiptStatus.InPool:
        return this.label('Receipt_Status_InPool', 'In pool');
      case ExpenseReceiptStatus.InClaim:
        return this.label('Receipt_Status_InClaim', 'In claim');
      case ExpenseReceiptStatus.Posted:
        return this.label('Receipt_Status_Posted', 'Posted');
      default:
        return this.label('Receipt_Status_Rejected', 'Rejected');
    }
  }

  statusSeverity(status: ExpenseReceiptStatus): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    switch (status) {
      case ExpenseReceiptStatus.InPool:
        return 'success';
      case ExpenseReceiptStatus.Incomplete:
        return 'warn';
      case ExpenseReceiptStatus.InClaim:
        return 'info';
      case ExpenseReceiptStatus.Rejected:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getRow(rowData: unknown): ExpenseReceipt {
    return rowData as ExpenseReceipt;
  }

  private optionLabel(o: ExpenseReceiptOwner): string {
    const kind =
      o.owner.entityDefinitionCode === ReceiptOwnerCode.PettyCashFund
        ? this.label('Receipt_Owner_Fund', 'Fund')
        : this.label('Receipt_Owner_Trip', 'Trip');
    return `${kind} · ${o.owner.number} · ${o.name}`;
  }

  private upsert(receipt: ExpenseReceipt): void {
    this.receipts.update((list) =>
      list.some((r) => r.id === receipt.id) ? list.map((r) => (r.id === receipt.id ? receipt : r)) : [receipt, ...list],
    );
  }

  private label(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }

  // #endregion
}
